/**
 * Activity analytics — the platform's report + settings API.
 *
 * The tracker's collect endpoint is not here: the shared tracker posts to it
 * itself (see `activity/hostTracker.ts`). These are the reads for the shared
 * ActivityReport screen, scoped by who is asking: `project` (Advanced →
 * Activity, and the Run view's System administration), `org` (an enterprise
 * administrator's people) and `instance` (instance administrators).
 */

import api from './client'
import type { ActivityMeta, ActivityReportAdapter } from '@twasta/page-runtime/activity/ActivityReport'

export type ActivityScope = 'project' | 'org' | 'instance'

export interface ActivitySettings {
  enabled: boolean
  realms: string[]
  consent: 'banner' | 'none'
  ip_mode: 'full' | 'truncate' | 'hash' | 'none' | null
  capture_clicks: boolean
  capture_scroll: boolean
}

export interface ActivitySettingsResponse {
  settings: ActivitySettings
  instance?: { enabled: boolean; ip_mode: string; retention_days: number; sink: string }
}

/** The report adapter for one scope (and project, for project scope). */
export function activityAdapter(scope: ActivityScope, project = ''): ActivityReportAdapter {
  const scoped = (params: Record<string, string> = {}) => ({
    ...params, scope, ...(project ? { project } : {}),
  })
  return {
    meta: async () => (await api.get<ActivityMeta>('/activity/meta', { params: scoped() })).data,
    report: async (kind, params) =>
      (await api.get<{ data: unknown }>(`/activity/report/${encodeURIComponent(kind)}`,
        { params: scoped(params) })).data,
  }
}

export async function getActivitySettings(project: string): Promise<ActivitySettingsResponse> {
  return (await api.get<ActivitySettingsResponse>(`/activity/settings/${encodeURIComponent(project)}`)).data
}

export async function saveActivitySettings(
  project: string, values: Partial<ActivitySettings>,
): Promise<ActivitySettingsResponse> {
  return (await api.put<ActivitySettingsResponse>(
    `/activity/settings/${encodeURIComponent(project)}`, values)).data
}
